import type { ChangeEvent } from 'react';
import type { ShopifyCouponTerms, ShopifyDiscountValueType } from '@discovr/features/shopify';
import { activeDatesSummary } from './shopifyCouponSummary';
import { formatStoreMoney, resolveCurrencyCode } from './shopifyCurrency';
import {
  shopifyActionsCopy,
  shopifyPurchaseTypeCopy,
  shopifyVariantsCopy,
} from '@discovr/features/copy';

const LATIN_FOLDS: Readonly<Record<string, string>> = {
  ß: 'SS',
  ẞ: 'SS',
  Æ: 'AE',
  æ: 'AE',
  Œ: 'OE',
  œ: 'OE',
  Ø: 'O',
  ø: 'O',
  Đ: 'D',
  đ: 'D',
  Ð: 'D',
  ð: 'D',
  Ł: 'L',
  ł: 'L',
  Þ: 'TH',
  þ: 'TH',
};
const LATIN_FOLD_PATTERN = new RegExp(`[${Object.keys(LATIN_FOLDS).join('')}]`, 'g');

/** Mirror of the BE's foldLatin (code.go): letters that don't decompose, then
 *  NFKD with the accents dropped. */
const foldLatin = (s: string) =>
  s
    .replace(LATIN_FOLD_PATTERN, (c) => LATIN_FOLDS[c] ?? c)
    .normalize('NFKD')
    .replace(/\p{Mn}/gu, '')
    .normalize('NFC');

/** Mirror of the BE's SanitizeCode (fold accents, uppercase, strip outside
 *  [A-Z0-9_-]) — used for the coupon-name-vs-config dirty check and the code
 *  preview. */
export const sanitizeCodeSegment = (s: string) =>
  foldLatin(s.trim())
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '');

/** Whichever discount value the code text embeds when "include in code" is
 *  on — the percentage, or the ₹ fixed amount. Mirrors the BE's
 *  GenerateInput.codeValue() so the FE preview and the actually-minted code
 *  can't drift. */
export function discountCodeValue(
  valueType: ShopifyDiscountValueType,
  pct: number,
  amount: number,
): number {
  return valueType === 'fixed_amount' ? amount : pct;
}

/** The base code Generate/Replace actually mints from `sanitizedName` —
 *  mirrors the BE's real scheme (DeriveCode/uniqueCode in
 *  internal/integrations/shopify): the sanitized coupon name, optionally
 *  suffixed with the rounded discount value when "include value in code" is
 *  on. There is no creator segment — the BE only embeds one when a live
 *  creator display name is available, which isn't something this preview can
 *  know ahead of Generate, and disambiguation between multiple creators'
 *  codes happens via a server-side "-2", "-3"… collision suffix this preview
 *  deliberately doesn't try to predict. Previewing the bare base code (what
 *  the FIRST code actually looks like) is accurate; guessing a creator name
 *  or a collision suffix would just be a different way to lie about the
 *  format. */
export function previewCode(
  sanitizedName: string,
  value: number,
  includeValueInCode: boolean,
): string {
  return `${sanitizedName}${includeValueInCode ? Math.round(value) : ''}`;
}

/** BE caps, from internal/integrations/shopify/code.go. */
const MAX_HANDLE_LEN = 10;
const MAX_CODE_LEN = 41;

/** Faithful port of the BE's DeriveCode, for the one caller that actually
 *  knows the creator: Change code replaces a SPECIFIC creator's coupon, so
 *  unlike Generate's pre-creator preview it can show the real shape —
 *  `NAME_HANDLE` with the value appended last, not `NAME15` with no handle.
 *  Typing "QASAVVY" previewed `QASAVVY` and minted `QASAVVY_STOCKSAVVY`.
 *
 *  The handle is the uppercased creator name stripped to alphanumerics only
 *  (stricter than sanitizeCodeSegment, so a display name can never inject
 *  `_` or `-` into the slot) and capped at 10. The server-side `-2`/`-3`
 *  collision suffix is deliberately not predicted; it only ever appears when
 *  the derived code is already taken. */
export function previewCreatorCode(
  sanitizedName: string,
  creatorName: string | undefined,
  value: number,
  includeValueInCode: boolean,
): string {
  const parts = sanitizedName ? [sanitizedName] : [];
  const handle = foldLatin(creatorName ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, MAX_HANDLE_LEN);
  if (handle) parts.push(handle);
  const code = `${parts.join('_')}${includeValueInCode ? Math.round(value) : ''}`;
  return code.slice(0, MAX_CODE_LEN);
}

/** Stand-in creator segment for the "Example:" previews, which are rendered
 *  before any creator is known. */
export const PREVIEW_CREATOR_HANDLE = 'CREATOR';

/** The example code shown next to the coupon-name field. Per-creator codes
 *  carry a creator segment (`TEST1_INVESTWITH` on prod); a campaign-wide
 *  shared code does not. Previewing the bare name for both taught the wrong
 *  shape to every brand in per-creator mode, which is the default. Routed
 *  through previewCreatorCode/previewCode so the example cannot drift from
 *  the BE's DeriveCode. */
export function previewExampleCode(
  sanitizedName: string,
  perCreator: boolean,
  value: number,
  includeValueInCode: boolean,
): string {
  return perCreator
    ? previewCreatorCode(sanitizedName, PREVIEW_CREATOR_HANDLE, value, includeValueInCode)
    : previewCode(sanitizedName, value, includeValueInCode);
}

/** The BE rejects a longer name (discovr#596); a 120-char name built a
 *  131-char code that overflowed every coupon table. */
export const COUPON_NAME_MAX = 20;

export const COUPON_NAME_EMPTY_MESSAGE = 'Enter a discount code name.';
export const COUPON_NAME_NO_ALNUM_MESSAGE = 'Include at least one letter or number.';

/** The message for a name the BE would reject as "must contain at least one
 *  letter or number", or '' when the name is fine. */
export function couponNameRequiredMessage(name: string): string {
  if (name.trim() === '') return COUPON_NAME_EMPTY_MESSAGE;
  return /[A-Z0-9]/.test(sanitizeCodeSegment(name)) ? '' : COUPON_NAME_NO_ALNUM_MESSAGE;
}

/** A replacement code starts now unless the old one's start is still ahead. */
export function replacementStartDate(
  start: string | undefined,
  now: number = Date.now(),
): string | undefined {
  if (!start) return undefined;
  return Date.parse(start) > now ? start : undefined;
}

/** Ceiling for "Minimum purchase amount" / "Minimum quantity of items".
 *  Shopify's own API doesn't publish a product-level max for either field
 *  (subtotal is an arbitrary-precision Decimal; quantity is a UnsignedInt64,
 *  max 2^64-1) — this mirrors the practical type ceiling our own float64
 *  field and JS's own number handling can carry safely, rather than
 *  inventing an arbitrary business limit. */
export const MAX_MINIMUM_REQUIREMENT_VALUE = Number.MAX_SAFE_INTEGER;

/** Mirrors the BE rule: percentage in (0, 100], fixed amount in (0, MAX]. */
export function discountValueError(
  valueType: string | undefined,
  pct: number,
  amount: number,
): string | undefined {
  if (valueType === 'fixed_amount') {
    if (!(amount > 0)) return 'Enter an amount above 0.';
    if (amount > MAX_MINIMUM_REQUIREMENT_VALUE) {
      return `Enter an amount of at most ${MAX_MINIMUM_REQUIREMENT_VALUE.toLocaleString()}.`;
    }
    return undefined;
  }
  return pct > 0 && pct <= 100 ? undefined : 'Enter a percentage above 0 and up to 100.';
}

/** `value`/`onChange` props for a fully-clearable number <Input>: 0 renders as
 *  an empty field (0 is treated as "no value"), and an empty or non-numeric
 *  entry maps back to 0. Single source for the discount-% inputs on both the
 *  settings form and the manage-coupon modal so the contract can't drift. */
export function clearableNumberProps(
  value: number,
  onValue: (n: number) => void,
): { value: number | ''; onChange: (e: ChangeEvent<HTMLInputElement>) => void } {
  return {
    value: value === 0 ? '' : value,
    onChange: (e) => {
      const n = Number(e.target.value);
      onValue(e.target.value === '' || Number.isNaN(n) ? 0 : n);
    },
  };
}

/** Sorts refs by id — multi-select product/collection targeting is a SET, not
 *  a sequence, so dirty-checking (form vs. last-saved config) must compare
 *  order-independently. Used by ShopifySettingsTab's normalizedFormJson. */
export const byId = <T extends { id: string }>(refs: T[]) =>
  [...refs].sort((a, b) => a.id.localeCompare(b.id));

/** Short "Applies to" label for a coupon's own terms snapshot (Coupons table
 *  cell) — not the campaign's current config, which may have since changed.
 *  `terms` is typed as always-present (every coupon is minted through
 *  Generate, which always builds one), but this still guards against a
 *  payload that violates that contract at runtime — a rolling-deploy window
 *  against an older BE binary, or a future write path that skips Generate —
 *  so a bad row degrades to "—" instead of crashing the whole table. */
export function couponTermsSummary(terms: ShopifyCouponTerms | null | undefined): string {
  if (!terms) return '—';
  if (terms.target_type === 'entire_store') return 'Entire store';
  if (terms.target_type === 'collection') {
    const n = terms.collections?.length ?? 0;
    return n === 0 ? 'Collection' : `${n} collection${n === 1 ? '' : 's'}`;
  }
  // Show the product name(s), not a bare count: one product → its name; more
  // than one → the first name plus "+N more".
  const products = productItems(terms);
  if (products.length === 0) return 'Product';
  const first = products[0]?.title ?? 'Product';
  return products.length === 1 ? first : `${first} +${products.length - 1} more`;
}

/** One targeted product/collection with the optional thumbnail the picker
 *  surfaces (BE sends image_url on the terms snapshot when available). */
export interface CouponTargetItem {
  id: string;
  title: string;
  image_url?: string;
}

/** Structured terms breakdown — the single source both the hover-string
 *  (couponTermsTooltip) and the "Applies to" popup render from, so the
 *  minimum / combines / active wording can never drift between them. `kind`
 *  is the target axis; `items` is empty for entire_store. */
export interface CouponTermsDetail {
  kind: ShopifyCouponTerms['target_type'];
  items: CouponTargetItem[];
  minimum: string;
  usageLimit: string;
  /** Present only when subscriptions are discounted. */
  purchase?: string;
  combines: string;
  active: string;
}

function termsMeta(
  terms: ShopifyCouponTerms,
  storeCurrencyCode?: string,
  storeTimezone?: string,
): Pick<CouponTermsDetail, 'minimum' | 'usageLimit' | 'purchase' | 'combines' | 'active'> {
  const currency = resolveCurrencyCode(terms.currency_code, storeCurrencyCode);
  const minimum =
    terms.minimum_requirement_type === 'amount'
      ? `${formatStoreMoney(terms.minimum_requirement_value ?? 0, currency)}+ subtotal`
      : terms.minimum_requirement_type === 'quantity'
        ? `${terms.minimum_requirement_value ?? 0}+ items`
        : 'None';

  const usageLimitBits: string[] = [];
  if (terms.usage_limit) {
    usageLimitBits.push(shopifyActionsCopy.totalUses(terms.usage_limit));
  }
  if (terms.applies_once_per_customer) usageLimitBits.push('one per customer');
  const usageLimit = usageLimitBits.length === 0 ? 'None' : usageLimitBits.join(', ');

  const combines =
    [
      terms.combines_with_product_discounts && 'product discounts',
      terms.combines_with_order_discounts && 'order discounts',
      terms.combines_with_shipping_discounts && 'shipping discounts',
    ]
      .filter(Boolean)
      .join(', ') || 'None';

  const active = activeDatesSummary(terms.start_date, terms.end_date, storeTimezone);

  const purchase =
    terms.purchase_type === 'subscription' || terms.purchase_type === 'both'
      ? terms.discount_kind === 'bxgy'
        ? shopifyPurchaseTypeCopy.bxgySummary(terms.purchase_type)
        : shopifyPurchaseTypeCopy.summary(terms.purchase_type, terms.recurring_cycle_limit ?? 0)
      : undefined;

  return { minimum, usageLimit, purchase, combines, active };
}

/** The target refs for a coupon's scope — collections, products, or none
 *  (entire store). Mirrors couponTermsSummary's target_type dispatch. */
/** Whole products, then variants named with their product. */
function productItems(terms: ShopifyCouponTerms): CouponTargetItem[] {
  return [
    ...(terms.products ?? []),
    ...(terms.variants ?? []).map((v) => ({
      id: v.id,
      title: shopifyVariantsCopy.chipLabel(v.product_title, v.title),
    })),
  ];
}

function targetItems(terms: ShopifyCouponTerms): CouponTargetItem[] {
  switch (terms.target_type) {
    case 'collection':
      return terms.collections ?? [];
    case 'entire_store':
      return [];
    default:
      return productItems(terms);
  }
}

/** Structured terms for the "Applies to" popup — target items (with optional
 *  thumbnails) plus the same minimum / combines / active lines the tooltip
 *  shows. Guards a falsy `terms` like couponTermsSummary does.
 *  `storeCurrencyCode` is the connected store's currency, used only as the
 *  fallback for a coupon minted before terms carried their own — see
 *  resolveCurrencyCode. */
export function couponTermsDetail(
  terms: ShopifyCouponTerms | null | undefined,
  storeCurrencyCode?: string,
  storeTimezone?: string,
): CouponTermsDetail {
  if (!terms) {
    return {
      kind: 'product',
      items: [],
      minimum: 'None',
      usageLimit: 'None',
      combines: 'None',
      active: 'Runs until stopped',
    };
  }
  return {
    kind: terms.target_type,
    items: targetItems(terms),
    ...termsMeta(terms, storeCurrencyCode, storeTimezone),
  };
}

/** Fallback line 1 when there is no item name to show. Typed on the real
 *  target_type union so a new target type fails to compile here instead of
 *  silently falling back at runtime. */
const TARGET_TYPE_LABEL: Record<ShopifyCouponTerms['target_type'], string> = {
  product: 'Product',
  collection: 'Collection',
  entire_store: 'Entire store',
};

const TARGET_TYPE_PLURAL: Record<ShopifyCouponTerms['target_type'], string> = {
  product: 'products',
  collection: 'collections',
  entire_store: 'items',
};

/** Shared "Applies to" cell content. One item shows its own name; several show
 *  "N products" with the names as a second line. Single source for
 *  ShopifySettingsTab's AppliesToCell and PublicShopifyCouponsTab's
 *  PublicAppliesToCell so the two views can't drift on this derivation. */
export function appliesToTitleAndNames(terms: ShopifyCouponTerms | null | undefined): {
  title: string;
  names: string;
} {
  // No currency argument: this derives only the target type + item names,
  // never a money value, so the currency fallback can't reach the output.
  const detail = couponTermsDetail(terms);
  const typeLabel = TARGET_TYPE_LABEL[detail.kind] ?? couponTermsSummary(terms);
  const titles = detail.items.map((it) => it.title).filter(Boolean);
  if (detail.items.length > 1) {
    return {
      title: `${detail.items.length} ${TARGET_TYPE_PLURAL[detail.kind]}`,
      names: titles.join(', '),
    };
  }
  return { title: titles[0] ?? typeLabel, names: '' };
}

/** Full terms breakdown as a single pre-line string — the public read-only
 *  coupons view uses it as a native `title` tooltip. Built from the same
 *  termsMeta so it never drifts from the interactive popup. See
 *  couponTermsSummary for why `terms` still guards against a falsy value. */
export function couponTermsTooltip(
  terms: ShopifyCouponTerms | null | undefined,
  storeCurrencyCode?: string,
  storeTimezone?: string,
): string {
  if (!terms) return 'Applies to: —';

  const targetNames = (refs: { title: string }[] | undefined, fallback: string) =>
    refs && refs.length > 0 ? refs.map((r) => r.title).join(', ') : fallback;
  const targets =
    terms.target_type === 'entire_store'
      ? 'Entire store'
      : terms.target_type === 'collection'
        ? targetNames(terms.collections, 'Collection')
        : targetNames(productItems(terms), 'Product');

  const { minimum, usageLimit, purchase, combines, active } = termsMeta(terms, storeCurrencyCode, storeTimezone);
  const purchaseLine = purchase ? `\n${shopifyPurchaseTypeCopy.summaryLabel}: ${purchase}` : '';
  return `Applies to: ${targets}\nMinimum: ${minimum}\nUsage limit: ${usageLimit}${purchaseLine}\nCombines with: ${combines}\nActive: ${active}\n${shopifyActionsCopy.cartRule}`;
}
